'use client'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import Image from 'next/image'
import { useState } from 'react'

interface VehicleCardProps {
	id: string
	name: string
	imageUrl: string
	transmission: 'Manual' | 'Automático'
	category?: string
	doors?: number
	passengers: number
	luggage: number
	fuelType?: string
	dailyRate: number
	totalRate: number
	onReserve?: (id: string) => void
	className?: string
}

export function VehicleCard({
	id,
	name,
	imageUrl,
	transmission,
	category,
	doors,
	passengers,
	luggage,
	fuelType,
	dailyRate,
	totalRate,
	onReserve,
	className,
}: VehicleCardProps) {
	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat('es-CO', {
			style: 'currency',
			currency: 'COP',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount)
	}

	const [imgError, setImgError] = useState(false)
	const hasImage = !!imageUrl && !imgError

	return (
		<Card
			className={cn(
				'group overflow-hidden border-2 border-[#09194D] bg-white flex flex-col transition-shadow hover:shadow-md',
				className,
			)}
		>
			{/* Image + Specs — separados, no superpuestos */}
			<div className='flex flex-col w-full bg-white'>
				{/* Specs grid */}
				<div className='w-[92%] mx-auto grid grid-cols-3 gap-y-2 gap-x-2 pt-3 text-white font-["Bricolage_Grotesque"] font-bold'>
					{[
						{ label: category || '-', value: 'categoría' },
						{
							label: transmission === 'Automático' ? 'AT' : 'MT',
							value: 'transmisión',
						},
						{ label: `${passengers}`, value: 'personas' },
						{ label: fuelType || '-', value: 'combustible' },
						{ label: `${luggage}`, value: 'maletas' },
						{ label: doors ? `${doors}` : '-', value: 'puertas' },
					].map((item, i) => (
						<div
							key={i}
							className='flex flex-col items-center justify-center rounded-md px-1 py-1 text-center leading-tight bg-[#09194D]/70 min-w-0'
						>
							<span
								className='block font-bold truncate w-full text-center'
								style={{ fontSize: 'clamp(12px, 1.2vw, 20px)' }}
							>
								{item.label}
							</span>
							{item.value && (
								<span
									className='block font-light opacity-90 truncate w-full text-center'
									style={{ fontSize: 'clamp(10px, 0.9vw, 16px)' }}
								>
									{item.value}
								</span>
							)}
						</div>
					))}
				</div>

				{/* Imagen */}
				<div className='relative w-full aspect-4/3 bg-white'>
					{hasImage ? (
						<Image
							src={imageUrl}
							alt={name}
							fill
							className='object-contain group-hover:scale-105 transition-transform'
							sizes='(max-width:768px) 100vw, (max-width:1200px) 33vw, 25vw'
							onError={() => setImgError(true)}
							referrerPolicy='no-referrer'
						/>
					) : (
						<div className='absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-400 text-xs'>
							{name}
						</div>
					)}
				</div>
			</div>

			{/* Name bar */}
			<div className='w-full bg-[#09194D] text-white text-center py-2 px-3'>
				<h3
					className='font-["Bricolage_Grotesque"] font-black tracking-wide truncate'
					style={{ fontSize: 'clamp(16px, 2vw, 36px)' }}
				>
					{name.toUpperCase()}
				</h3>
			</div>

			{/* Pricing + CTA */}
			<div className='flex flex-col gap-3 p-4 pb-6 flex-1'>
				<div className='flex justify-between items-start font-["Bricolage_Grotesque"] text-[#09194D]'>
					<div className='flex flex-col'>
						<span
							className='font-bold leading-tight'
							style={{ fontSize: 'clamp(13px, 1.2vw, 20px)' }}
						>
							Tarifa Día
						</span>
						<span
							className='font-medium opacity-80'
							style={{ fontSize: 'clamp(12px, 1.1vw, 18px)' }}
						>
							{formatCurrency(dailyRate)}
						</span>
					</div>
					<div className='flex flex-col items-end'>
						<span
							className='font-bold leading-tight'
							style={{ fontSize: 'clamp(13px, 1.2vw, 20px)' }}
						>
							Tarifa Total
						</span>
						<span
							className='font-black tracking-tight'
							style={{ fontSize: 'clamp(16px, 1.8vw, 30px)' }}
						>
							{formatCurrency(totalRate)}
						</span>
					</div>
				</div>

				<p
					className='text-center text-[#09194D] opacity-80 font-["Bricolage_Grotesque"]'
					style={{ fontSize: 'clamp(11px, 1vw, 18px)' }}
				>
					*Kilometraje ilimitado*
				</p>

				<div className='flex items-center justify-center'>
					<Button
						onClick={() => onReserve?.(id)}
						className='mt-1 w-full bg-[#CDFF00] hover:bg-[#CDFF00]/90 text-[#09194D] font-["Bricolage_Grotesque"] font-black py-3 h-auto rounded-full cursor-pointer'
						style={{ fontSize: 'clamp(14px, 1.8vw, 32px)' }}
					>
						¡RESÉRVALO YA!
					</Button>
				</div>
			</div>
		</Card>
	)
}